import { dropDisc, getValidColumns, winningLineAt } from './board';
import { Board, COLS, CONNECT, Disc, ROWS, opponent } from './types';

export type Difficulty = 'easy' | 'medium' | 'hard';

/** Search depth (plies looked ahead) per difficulty. */
const DEPTH_BY_DIFFICULTY: Record<Difficulty, number> = {
  easy: 2,
  medium: 5,
  hard: 7,
};

/** Probability that "easy" makes a random (non-optimal) move, for a gentler opponent. */
const EASY_BLUNDER_CHANCE = 0.3;

const WIN_SCORE = 1_000_000;
const CENTER_COL = Math.floor(COLS / 2);

/**
 * Scores a single 4-cell window from `disc`'s perspective.
 * Windows containing both players' discs have no completion potential.
 */
function scoreWindow(cells: number[], disc: Disc): number {
  const foe = opponent(disc);
  let mine = 0;
  let theirs = 0;
  let empty = 0;
  for (const c of cells) {
    if (c === disc) mine++;
    else if (c === foe) theirs++;
    else empty++;
  }

  if (mine > 0 && theirs > 0) return 0; // blocked window
  if (mine === CONNECT) return WIN_SCORE;
  if (mine === 3 && empty === 1) return 100;
  if (mine === 2 && empty === 2) return 10;
  if (theirs === 3 && empty === 1) return -120; // value blocking just above own threats
  if (theirs === 2 && empty === 2) return -10;
  return 0;
}

/**
 * Static evaluation of a non-terminal board from `disc`'s perspective.
 * Sums the score of every 4-cell window plus a small center-column bonus.
 */
export function evaluate(board: Board, disc: Disc): number {
  let score = 0;

  // Prefer central control: discs there belong to more potential lines.
  for (let row = 0; row < ROWS; row++) {
    if (board[row][CENTER_COL] === disc) score += 6;
  }

  // Horizontal windows.
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col <= COLS - CONNECT; col++) {
      score += scoreWindow(
        [board[row][col], board[row][col + 1], board[row][col + 2], board[row][col + 3]],
        disc,
      );
    }
  }
  // Vertical windows.
  for (let col = 0; col < COLS; col++) {
    for (let row = 0; row <= ROWS - CONNECT; row++) {
      score += scoreWindow(
        [board[row][col], board[row + 1][col], board[row + 2][col], board[row + 3][col]],
        disc,
      );
    }
  }
  // Diagonal down-right windows.
  for (let row = 0; row <= ROWS - CONNECT; row++) {
    for (let col = 0; col <= COLS - CONNECT; col++) {
      score += scoreWindow(
        [
          board[row][col],
          board[row + 1][col + 1],
          board[row + 2][col + 2],
          board[row + 3][col + 3],
        ],
        disc,
      );
    }
  }
  // Diagonal down-left windows.
  for (let row = 0; row <= ROWS - CONNECT; row++) {
    for (let col = CONNECT - 1; col < COLS; col++) {
      score += scoreWindow(
        [
          board[row][col],
          board[row + 1][col - 1],
          board[row + 2][col - 2],
          board[row + 3][col - 3],
        ],
        disc,
      );
    }
  }

  return score;
}

/** Orders columns center-outward so alpha-beta prunes more aggressively. */
function orderColumns(cols: number[]): number[] {
  return [...cols].sort((a, b) => Math.abs(a - CENTER_COL) - Math.abs(b - CENTER_COL));
}

/**
 * Minimax with alpha-beta pruning. `aiDisc` is the player we maximize for.
 * `lastMove` is the move that produced `board`, used for fast terminal detection.
 * Terminal wins are offset by `depth` so the search prefers faster wins and
 * slower losses.
 */
function minimax(
  board: Board,
  depth: number,
  alpha: number,
  beta: number,
  maximizing: boolean,
  aiDisc: Disc,
  lastMove: { row: number; col: number } | null,
): number {
  if (lastMove) {
    const line = winningLineAt(board, lastMove.row, lastMove.col);
    if (line) {
      const winner = board[lastMove.row][lastMove.col] as Disc;
      return winner === aiDisc ? WIN_SCORE + depth : -(WIN_SCORE + depth);
    }
  }

  const valid = getValidColumns(board);
  if (depth === 0 || valid.length === 0) {
    return evaluate(board, aiDisc);
  }

  const moveDisc: Disc = maximizing ? aiDisc : opponent(aiDisc);

  if (maximizing) {
    let value = -Infinity;
    for (const col of orderColumns(valid)) {
      const res = dropDisc(board, col, moveDisc)!;
      value = Math.max(
        value,
        minimax(res.board, depth - 1, alpha, beta, false, aiDisc, { row: res.row, col }),
      );
      alpha = Math.max(alpha, value);
      if (alpha >= beta) break;
    }
    return value;
  }

  let value = Infinity;
  for (const col of orderColumns(valid)) {
    const res = dropDisc(board, col, moveDisc)!;
    value = Math.min(
      value,
      minimax(res.board, depth - 1, alpha, beta, true, aiDisc, { row: res.row, col }),
    );
    beta = Math.min(beta, value);
    if (alpha >= beta) break;
  }
  return value;
}

/**
 * Chooses the column the AI (`aiDisc`) should play on `board`.
 * Deterministic except for "easy", which occasionally plays randomly.
 * Throws if the board is full (no legal move).
 *
 * NOTE: takes an optional `random` source so tests can stay deterministic.
 */
export function chooseMove(
  board: Board,
  aiDisc: Disc,
  difficulty: Difficulty = 'medium',
  random: () => number = Math.random,
): number {
  const valid = getValidColumns(board);
  if (valid.length === 0) {
    throw new Error('chooseMove called on a full board: no legal moves.');
  }

  if (difficulty === 'easy' && random() < EASY_BLUNDER_CHANCE) {
    return valid[Math.floor(random() * valid.length)];
  }

  const depth = DEPTH_BY_DIFFICULTY[difficulty];
  let bestCol = valid[0];
  let bestScore = -Infinity;
  let alpha = -Infinity;
  const beta = Infinity;

  for (const col of orderColumns(valid)) {
    const res = dropDisc(board, col, aiDisc)!;
    const score = minimax(res.board, depth - 1, alpha, beta, false, aiDisc, {
      row: res.row,
      col,
    });
    if (score > bestScore) {
      bestScore = score;
      bestCol = col;
    }
    alpha = Math.max(alpha, bestScore);
  }

  return bestCol;
}
